import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { ORAKLE_WEATHER_LOGO_URL } from '../../constants/colors';
import {
    FormInput,
    FormButton,
} from '../Form';
import '../Form/form-components.css';
import '../../pages/admin/AdminAuth.css';

const AuthLoginPage = ({
    title = 'Connexion',
    subtitle = 'Connectez-vous à votre tableau de bord',
    redirectPath = '/dashboard/operations',
    mfaPath = '/admin/mfa',
    resetPasswordPath = '/admin/reset-password',
    showLogo = true,
    storeEmailOnLogin = false,
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [localError, setLocalError] = useState(null);

    const { login, isLoading, error, clearError, isAuthenticated, mfaRequired, sessionId } = useAdminAuth();
    const navigate = useNavigate();

    useEffect(() => {
        clearError();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isAuthenticated && !mfaRequired) {
            navigate(redirectPath);
        } else if (mfaRequired && sessionId) {
            navigate(mfaPath, { state: { session_id: sessionId } });
        }
    }, [isAuthenticated, mfaRequired, sessionId, navigate, redirectPath, mfaPath]);

    const validate = () => {
        const errors = {};
        if (!email.trim()) errors.email = 'L\'adresse email est requise';
        if (!password) errors.password = 'Le mot de passe est requis';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLocalError(null);
        clearError();

        if (!validate()) return;

        try {
            if (storeEmailOnLogin) {
                localStorage.setItem('admin_email', email);
            }
            await login({ email, password });
        } catch (err) {
            setLocalError(err.message || 'Une erreur est survenue');
        }
    };

    const displayError = error || localError;
    const isAuthError = displayError && (
        displayError.toLowerCase().includes('incorrect')
        || displayError.toLowerCase().includes('invalid')
        || displayError.toLowerCase().includes('401')
        || displayError.toLowerCase().includes('mot de passe')
        || displayError.toLowerCase().includes('email')
    );

    const globalErrorMessage = isAuthError
        ? 'Email ou mot de passe incorrect'
        : displayError;

    return (
        <div className="auth-page">
            <div className="auth-page__card">
                {showLogo && (
                    <div className="auth-page__logo">
                        <img
                            src={ORAKLE_WEATHER_LOGO_URL}
                            alt="Orakle Weather"
                        />
                    </div>
                )}

                <div className="auth-page__header">
                    <h1>{title}</h1>
                    {subtitle && <p>{subtitle}</p>}
                </div>

                <form onSubmit={handleSubmit} className="auth-page__form" noValidate>
                    {globalErrorMessage && (
                        <div className="ow-form-alert ow-form-alert--error" role="alert">
                            {globalErrorMessage}
                        </div>
                    )}

                    <FormInput
                        label="Adresse email"
                        name="email"
                        type="email"
                        placeholder="votre@email.com"
                        value={email}
                        onChange={(val) => {
                            setEmail(val);
                            if (fieldErrors.email) {
                                setFieldErrors((prev) => ({ ...prev, email: undefined }));
                            }
                        }}
                        error={fieldErrors.email}
                        disabled={isLoading}
                        required
                        autoFocus
                        icon={<FiMail size={16} />}
                        id="auth-email"
                    />

                    <FormInput
                        label="Mot de passe"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(val) => {
                            setPassword(val);
                            if (fieldErrors.password) {
                                setFieldErrors((prev) => ({ ...prev, password: undefined }));
                            }
                        }}
                        error={fieldErrors.password}
                        disabled={isLoading}
                        required
                        endIcon={(
                            <button
                                type="button"
                                className="ow-form-input-icon-btn"
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                tabIndex={-1}
                            >
                                {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                            </button>
                        )}
                        id="auth-password"
                    />

                    <FormButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        fullWidth
                        loading={isLoading}
                        className="auth-page__submit"
                    >
                        {isLoading ? 'Connexion...' : 'Se connecter'}
                    </FormButton>

                    <div className="auth-page__links">
                        <button
                            type="button"
                            className="auth-page__link"
                            onClick={() => navigate(resetPasswordPath)}
                            disabled={isLoading}
                        >
                            Mot de passe oublié ?
                        </button>
                    </div>
                </form>
            </div>

            <p className="auth-page__footer">
                © {new Date().getFullYear()} Orakle Weather
            </p>
        </div>
    );
};

export default AuthLoginPage;
